import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { TextField } from "../admin/fields";
import { SelectField } from "../../components/SelectField";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useEresDbs } from "./EresDbPanel";
import { DESIGNS, designById } from "../../lib/designs";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

const Properties = ({ provider, dbId, codes, setCodes }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["properties", provider, dbId],
    queryFn: () => api.get("/platform/properties", { params: { provider, eres_db_id: dbId } }).then((r) => r.data),
    enabled: provider === "mock" || !!dbId, retry: false,
  });
  const toggle = (id, on) => setCodes(on ? [...codes, id] : codes.filter((c) => c !== id));
  if (provider === "eres" && !dbId) return <p className="text-sm text-ink/55">Choose an eRes database to see its properties.</p>;
  if (isLoading) return <Loader2 className="h-5 w-5 animate-spin text-brand" />;
  if (error) return <p className="text-sm text-red-600" data-testid="properties-error">{apiError(error)}</p>;
  return (
    <div className="max-h-56 space-y-2 overflow-y-auto rounded-xl border border-ink/10 p-3" data-testid="properties-list">
      {data.map((h) => (
        <label key={h.id} className="flex cursor-pointer items-center gap-3 text-sm">
          <Checkbox checked={codes.includes(h.id)} onCheckedChange={(v) => toggle(h.id, !!v)} data-testid={`property-${h.id}`} />
          <span>{h.name}</span><span className="font-mono text-xs text-ink/45">{h.id}{h.city ? ` · ${h.city}` : ""}</span>
        </label>
      ))}
      {data.length === 0 && <p className="text-sm text-ink/55">No properties found in this database.</p>}
    </div>
  );
};

const CURRENCIES = ["KES", "USD", "EUR", "GBP", "TZS", "UGX", "RWF", "ZAR"].map((c) => ({ value: c, label: c === "KES" ? "KES (Kenya shillings, M-Pesa available)" : c }));

const RateCodes = ({ form, setCodes }) => {
  const { provider, eres_db_id: dbId, hotel_codes: hotels, currency, rate_codes: codes = [] } = form;
  const { data, isFetching, error } = useQuery({
    queryKey: ["rate-codes", provider, dbId, hotels.join(","), currency],
    queryFn: () => api.get("/platform/rate-codes", { params: { provider, eres_db_id: dbId, hotel_codes: hotels.join(","), currency } }).then((r) => r.data),
    enabled: hotels.length > 0 && (provider === "mock" || !!dbId), retry: false,
  });
  if (!hotels.length) return <p className="text-sm text-ink/55">Choose properties first.</p>;
  if (isFetching) return <p className="flex items-center gap-2 text-sm text-ink/55"><Loader2 className="h-4 w-4 animate-spin text-brand" />Reading {currency} rates from eRes…</p>;
  if (error) return <p className="text-sm text-red-600" data-testid="rate-codes-error">{apiError(error)}</p>;
  const toggle = (id, on) => setCodes(on ? [...codes, id] : codes.filter((c) => c !== id));
  return (
    <div className="space-y-2 rounded-xl border border-ink/10 p-3" data-testid="rate-codes-list">
      <p className="text-xs text-ink/55">{codes.length ? `${codes.length} selected: only these rates show on the booking engine.` : "None ticked: every rate eRes offers in this currency is shown."}</p>
      {data?.map((r) => (
        <label key={r.id} className="flex cursor-pointer items-center gap-3 text-sm">
          <Checkbox checked={codes.includes(r.id)} onCheckedChange={(v) => toggle(r.id, !!v)} data-testid={`rate-code-${r.id}`} />
          <span>{r.name}</span><span className="font-mono text-xs text-ink/45">{r.id} · {r.currency}</span>
        </label>
      ))}
      {data?.length === 0 && <p className="text-sm text-ink/55" data-testid="rate-codes-empty">eRes returned no {currency} rates for the next 3 months on this channel.</p>}
    </div>
  );
};

export const SiteDialog = ({ site, onClose, onCreated }) => {
  const isNew = !site.slug;
  const { data: dbs } = useEresDbs();
  const [form, setForm] = useState(isNew
    ? { name: "", slug: "", provider: "eres", eres_db_id: "", hotel_codes: [], currency: "KES", rate_codes: [], design: "classic", admin_name: "", admin_email: "", send_email: true, auto: true }
    : { currency: "KES", rate_codes: [], ...site });
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  const save = async () => {
    setBusy(true);
    try {
      const base = { name: form.name, provider: form.provider, eres_db_id: form.eres_db_id, hotel_codes: form.hotel_codes, currency: form.currency, rate_codes: form.rate_codes };
      if (isNew) {
        const { data } = await api.post("/platform/sites", { ...base, slug: form.slug, design: form.design, theme: designById(form.design).theme, admin_name: form.admin_name, admin_email: form.admin_email, send_email: form.send_email });
        onCreated(data);
      } else {
        await api.put(`/platform/sites/${site.slug}`, { ...base, status: form.status });
        toast.success("Site updated");
      }
      qc.invalidateQueries({ queryKey: ["platform-sites"] });
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };
  const dbOptions = [{ value: "none", label: "Select database" }, ...(dbs || []).map((d) => ({ value: d.id, label: d.name }))];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto font-sans sm:max-w-2xl" data-testid="site-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Create booking site" : `Edit ${site.name}`}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label="Hotel / group name" value={form.name} testid="site-name"
            onChange={(name) => set(isNew && form.auto ? { name, slug: slugify(name) } : { name })} />
          {isNew ? (
            <label className="field"><span className="field-label">Link name</span>
              <input className="field-input font-mono" value={form.slug} onChange={(e) => set({ slug: slugify(e.target.value), auto: false })} data-testid="site-slug" />
              <span className="text-xs text-ink/50">{window.location.origin}/s/{form.slug || "…"}</span>
            </label>
          ) : (
            <SelectField label="Status" value={form.status} testid="site-status-select" onChange={(status) => set({ status })}
              options={[{ value: "active", label: "Active" }, { value: "disabled", label: "Disabled (site and admin offline)" }]} />
          )}
          <SelectField label="Data source" value={form.provider} testid="site-provider-select" onChange={(provider) => set({ provider, hotel_codes: [], rate_codes: [] })}
            options={[{ value: "eres", label: "Live eRes" }, { value: "mock", label: "Demo data (no real bookings)" }]} />
          {form.provider === "eres" && (
            <SelectField label="eRes database" value={form.eres_db_id || "none"} options={dbOptions} testid="site-db-select" onChange={(v) => set({ eres_db_id: v === "none" ? "" : v, hotel_codes: [], rate_codes: [] })} />
          )}
          <SelectField label="Booking currency" value={form.currency} options={CURRENCIES} testid="site-currency-select" onChange={(currency) => set({ currency, rate_codes: [] })} />
          {isNew && <SelectField label="Starting design" value={form.design} testid="site-design-select" onChange={(design) => set({ design })}
            options={DESIGNS.map((d) => ({ value: d.id, label: d.name }))} />}
        </div>
        <div>
          <p className="field-label mb-2">Properties on this site</p>
          <Properties provider={form.provider} dbId={form.eres_db_id} codes={form.hotel_codes} setCodes={(hotel_codes) => set({ hotel_codes, rate_codes: [] })} />
        </div>
        <div>
          <p className="field-label mb-2">Rate codes linked to this booking engine ({form.currency})</p>
          <RateCodes form={form} setCodes={(rate_codes) => set({ rate_codes })} />
        </div>
        {isNew && (
          <div className="grid gap-4 border-t border-ink/10 pt-4 sm:grid-cols-2">
            <TextField label="Hotel admin name" value={form.admin_name} onChange={(admin_name) => set({ admin_name })} testid="site-admin-name" />
            <TextField label="Hotel admin email" value={form.admin_email} onChange={(admin_email) => set({ admin_email })} testid="site-admin-email" />
            <label className="flex cursor-pointer items-center gap-2 text-sm sm:col-span-2">
              <Checkbox checked={form.send_email} onCheckedChange={(v) => set({ send_email: !!v })} data-testid="site-send-email" />
              Email the link and login to the hotel admin
            </label>
          </div>
        )}
        <button className="btn-primary" onClick={save} disabled={busy} data-testid="site-save">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}{isNew ? "Create site" : "Save changes"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
